import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { getMuxHostedThumbnailUrl } from '@src/utility/story';

import type ApiClient from '../apiClient/ApiClient';
import type { TranscriptVoiceMapping } from '../types.ts/transcript';
import type { Client, SimpleSchemaTypes } from '@datocms/cma-client-browser';
import type { GraphQLClient } from 'graphql-request';

// Get all speakers for an album (showcase)
const ALBUM_SPEAKERS_QUERY = `query getAlbumSpeakers($albumId: ItemId) {
  showcase(filter: {id: {eq: $albumId}}) {
    id
    title
    speakers {
      id
      name
      title
      bio
      voice
      gender
      aiVoices {
        id
      }
      createdByUser {
        id
        name
      }
      headshots {
        id
        url
        responsiveImage {
          srcSet
        }
      }
    }
  }
}`;

// List which of an album's stories reference each speaker, with enough video
// metadata to render a thumbnail + duration for each story.
const ALBUM_SPEAKER_USAGE_QUERY = `query getAlbumSpeakerUsage($albumId: ItemId) {
  showcase(filter: {id: {eq: $albumId}}) {
    id
    stories {
      id
      title
      speakers {
        id
      }
      thumbnail {
        url
      }
      originalVideo {
        video {
          duration
          thumbnailUrl
          muxPlaybackId
        }
      }
      finalVideo {
        thumbnail {
          url
        }
        videoFilePrimary {
          video {
            duration
            thumbnailUrl
            muxPlaybackId
          }
        }
      }
    }
  }
}`;

// Get a story's album and its speakers
const STORY_SPEAKERS_QUERY = `query getStorySpeakers($storyId: ItemId) {
  story(filter: {id: {eq: $storyId}}) {
    id
    title
    speakers {
      id
      name
      title
      bio
      voice
      gender
      headshots {
        id
        url
        responsiveImage {
          srcSet
        }
      }
    }
    primaryShowcase {
      id
      title
      speakers {
        id
        name
        title
        bio
        voice
        gender
        headshots {
          id
          url
          responsiveImage {
            srcSet
          }
        }
      }
    }
  }
}`;

export interface DatoSpeakerHeadshot {
  id: string;
  url: string;
  responsiveImage?: {
    srcSet: string;
  };
}

/**
 * A speaker's gender (PLAT-11692), used for grammatical agreement when their
 * words are translated. Values match `ai_voice.gender` so a voice and the
 * person it dubs can be compared directly. Absent/null means "not set" — a
 * distinct state from any answer, and the one every pre-existing speaker is in.
 */
export type SpeakerGender = 'male' | 'female' | 'neutral';

export interface DatoSpeaker {
  id: string;
  name: string;
  title?: string;
  bio?: string;
  voice?: string;
  gender?: SpeakerGender | null;
  /** Mapped AI Voices (`ai_voices` links); used for the Speaker Manager count. */
  aiVoices?: Array<{ id: string }>;
  headshots?: DatoSpeakerHeadshot[];
  /** The person who created this speaker (from DatoCMS `created_by_user`). */
  createdByUser?: { id: string; name?: string } | null;
}

/** A story a speaker appears in, surfaced in the Speaker Manager's expand row. */
export interface SpeakerStoryRef {
  id: string;
  title: string;
  thumbnailUrl?: string;
  durationSeconds?: number;
}

/**
 * A speaker's AI Voice association (PLAT-10492, DE4/DI19):
 *   - `aiVoiceIds`   ordered `ai_voice` IDs; index 0 is the language-agnostic
 *                    default (head auto-promotes when removed).
 *   - `languagePrefs` optional per-language pin, { [languageId]: aiVoiceId },
 *                    layered over the ordered list.
 * Voice records themselves are resolved from the workspace catalog/activation
 * pool, so only IDs live here.
 */
export interface SpeakerAiVoiceMapping {
  aiVoiceIds: string[];
  languagePrefs: Record<string, string>;
}

interface AlbumSpeakersResponse {
  showcase: {
    id: string;
    title: string;
    speakers: DatoSpeaker[];
  };
}

interface UsageStoryVideo {
  video: {
    duration?: number;
    thumbnailUrl?: string;
    muxPlaybackId?: string;
  } | null;
}

interface AlbumSpeakerUsageResponse {
  showcase: {
    id: string;
    stories: Array<{
      id: string;
      title: string;
      speakers: Array<{ id: string }>;
      thumbnail?: { url: string } | null;
      originalVideo?: UsageStoryVideo | null;
      finalVideo?: {
        thumbnail?: { url: string } | null;
        videoFilePrimary?: UsageStoryVideo | null;
      } | null;
    }>;
  } | null;
}

interface StorySpeakersResponse {
  story: {
    id: string;
    title: string;
    speakers: DatoSpeaker[];
    primaryShowcase: {
      id: string;
      title: string;
      speakers: DatoSpeaker[];
    };
  };
}

/**
 * Coerce the `ai_voice_language_prefs` JSON field into a { languageId: aiVoiceId }
 * map. DatoCMS json fields round-trip as a serialized string; tolerate an
 * already-parsed object and fall back to {} on anything malformed.
 */
function parseLanguagePrefs(
  raw: string | Record<string, string> | null | undefined,
): Record<string, string> {
  if (!raw) return {};
  const obj: unknown = typeof raw === 'object' ? raw : safeJsonParse(raw);
  // Reject non-objects and arrays (typeof [] === 'object'), and keep only
  // string→string entries so a corrupt field can't leak a bad-shaped "map".
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return {};
  return Object.fromEntries(
    Object.entries(obj as Record<string, unknown>).filter(
      ([, v]) => typeof v === 'string',
    ),
  ) as Record<string, string>;
}

function safeJsonParse(raw: string): unknown {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/**
 * Repository for managing speakers and speaker mappings
 */
export class SpeakerRepository {
  private dClient: Client | ApiClient;
  private gqlClient: GraphQLClient;
  private logger = LogManager.getLogger('repository:SpeakerRepository');

  private cache = new Map<
    string,
    {
      data: {
        albumId: string;
        storySpeakers: DatoSpeaker[];
        albumSpeakers: DatoSpeaker[];
      };
      timestamp: number;
    }
  >();
  private CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

  constructor(dClient: Client | ApiClient, gqlClient: GraphQLClient) {
    this.dClient = dClient;
    this.gqlClient = gqlClient;
  }

  /**
   * Get all speakers for an album
   */
  async getAlbumSpeakers(albumId: string): Promise<DatoSpeaker[]> {
    try {
      const cacheKey = `albumSpeakers:${albumId}`;
      const cachedData = this.cache.get(cacheKey);
      if (cachedData && Date.now() - cachedData.timestamp < this.CACHE_TTL_MS) {
        return cachedData.data.albumSpeakers;
      }

      const response = await this.gqlClient.request<AlbumSpeakersResponse>(
        ALBUM_SPEAKERS_QUERY,
        { albumId },
      );

      this.cache.set(cacheKey, {
        data: {
          albumId,
          albumSpeakers: response.showcase.speakers || [],
          storySpeakers: [],
        },
        timestamp: Date.now(),
      });

      return response.showcase.speakers || [];
    } catch (error) {
      this.logger.error(
        'Error fetching album speakers',
        extractErrorInfo(error),
      );
      return [];
    }
  }

  /**
   * List which of the album's stories reference each speaker.
   * Derived from DatoCMS `story.speakers[]` — no backend endpoint required.
   * Returns a map of speakerId -> the stories using that speaker (the UI
   * derives the "used in N stories" count from the array length).
   */
  async getSpeakerStoryUsage(
    albumId: string,
  ): Promise<Record<string, SpeakerStoryRef[]>> {
    try {
      const response = await this.gqlClient.request<AlbumSpeakerUsageResponse>(
        ALBUM_SPEAKER_USAGE_QUERY,
        { albumId },
      );
      const usage: Record<string, SpeakerStoryRef[]> = {};
      for (const story of response.showcase?.stories ?? []) {
        // Resolve thumbnail once per story (mirrors the priority chain in
        // resolveDashboardStoryThumbnailUrl) so each reference can render a
        // Story-Manager-style card rather than a bare title.
        const finalVideo = story.finalVideo?.videoFilePrimary?.video;
        const originalVideo = story.originalVideo?.video;
        const muxPlaybackId =
          finalVideo?.muxPlaybackId ?? originalVideo?.muxPlaybackId;
        const thumbnailUrl =
          story.thumbnail?.url ??
          story.finalVideo?.thumbnail?.url ??
          finalVideo?.thumbnailUrl ??
          originalVideo?.thumbnailUrl ??
          (muxPlaybackId ? getMuxHostedThumbnailUrl(muxPlaybackId) : undefined);
        const durationSeconds = finalVideo?.duration ?? originalVideo?.duration;

        for (const speaker of story.speakers ?? []) {
          (usage[speaker.id] ??= []).push({
            id: story.id,
            title: story.title,
            thumbnailUrl,
            durationSeconds,
          });
        }
      }
      return usage;
    } catch (error) {
      this.logger.error(
        'Error fetching speaker story usage',
        extractErrorInfo(error),
      );
      return {};
    }
  }

  /**
   * Get all speakers for a story's album
   */
  async getStorySpeakers(storyId: string): Promise<{
    albumId: string;
    storySpeakers: DatoSpeaker[];
    albumSpeakers: DatoSpeaker[];
  }> {
    try {
      const cacheKey = `storySpeakers:${storyId}`;
      const cachedData = this.cache.get(cacheKey);
      if (cachedData && Date.now() - cachedData.timestamp < this.CACHE_TTL_MS) {
        return cachedData.data;
      }

      const response = await this.gqlClient.request<StorySpeakersResponse>(
        STORY_SPEAKERS_QUERY,
        { storyId },
      );

      const story = response.story;
      const showcase = story?.primaryShowcase;

      const data = {
        albumId: showcase?.id || '',
        storySpeakers: story?.speakers || [],
        albumSpeakers: showcase?.speakers || [],
      };

      this.cache.set(cacheKey, {
        data,
        timestamp: Date.now(),
      });

      return data;
    } catch (error) {
      this.logger.error(
        'Error fetching story speakers',
        extractErrorInfo(error),
      );
      return { albumId: '', storySpeakers: [], albumSpeakers: [] };
    }
  }

  /**
   * Create a new speaker in Dato CMS and add reference to the album
   */
  async createSpeaker(
    name: string,
    title?: string,
    bio?: string,
    voice?: string,
    additionalData?: {
      userId?: string | null;
      storyId?: string | null;
      albumId?: string | null;
    },
    headshots?: Array<{ upload_id: string }> | null,
    gender?: SpeakerGender | null,
  ): Promise<DatoSpeaker | null> {
    try {
      const itemType = await this.dClient.itemTypes.find('speaker');

      const speaker = await this.dClient.items.create({
        item_type: { type: 'item_type', id: itemType.id },
        name,
        title,
        bio,
        voice,
        // Omitted rather than nulled when unset, so a speaker created without
        // a gender is indistinguishable from every speaker that predates the
        // field (PLAT-11692).
        ...(gender ? { gender } : {}),
        created_by_user: additionalData?.userId ?? null,
        created_for_story: additionalData?.storyId ?? null,
        created_for_album: additionalData?.albumId ?? null,
        // DatoCMS gallery fields accept an array of { upload_id: string }
        ...(headshots ? { headshots } : {}),
      });

      return speaker as unknown as DatoSpeaker;
    } catch (error) {
      this.logger.error('Error creating speaker', extractErrorInfo(error));
      return null;
    } finally {
      this.clearCache();
    }
  }

  /**
   * Remove speaker reference from DatoCMS if it's no longer used in story
   *
   * @param storyId - Story ID
   * @param datoSpeakerId - The DatoCMS speaker ID to potentially remove
   * @param currentMappings - Current speaker mappings to check if speaker is still used
   */
  async checkAndRemoveSpeakerReferenceIfUnused(
    storyId: string,
    datoSpeakerId: string,
    currentMappings: TranscriptVoiceMapping,
  ): Promise<boolean> {
    try {
      // Check if the speaker is used anywhere else in the transcript
      const speakerStillUsed = Object.values(currentMappings.mappings).includes(
        datoSpeakerId,
      );

      if (!speakerStillUsed) {
        // If the speaker is not used anywhere else, remove the reference from the story
        await this.removeSpeakerReferenceFromStoryOrAlbum(
          datoSpeakerId,
          storyId,
        );
      }

      return true;
    } catch (error) {
      this.logger.error(
        'Error checking speaker references',
        extractErrorInfo(error),
      );
      return false;
    } finally {
      this.clearCache();
    }
  }

  /**
   * Add a speaker reference to an album
   */
  async addSpeakerReferenceToAlbum(
    datoSpeakerId: string,
    albumId: string,
  ): Promise<void> {
    try {
      // Get the album to retrieve current speakers
      const album = (await this.dClient.items.find(
        albumId,
      )) as SimpleSchemaTypes.Item & { speakers?: string[] };

      // Check if the speaker is already in the album
      const existingSpeakerIds = album.speakers || [];
      if (existingSpeakerIds.includes(datoSpeakerId)) {
        return; // Speaker already in album, no need to update
      }

      // Add the speaker to the album
      await this.dClient.items.update(albumId, {
        speakers: [...existingSpeakerIds, datoSpeakerId],
      });

      // Publish the album
      await this.dClient.items.publish(albumId);
    } catch (error) {
      this.logger.error(
        'Error adding speaker reference to album',
        extractErrorInfo(error),
      );
      throw error;
    } finally {
      this.clearCache();
    }
  }

  /**
   * Add a speaker reference to a story
   */
  async addSpeakerReferenceToStory(
    datoSpeakerId: string,
    storyId: string,
  ): Promise<void> {
    try {
      // Get the story to retrieve current speakers
      const story = (await this.dClient.items.find(
        storyId,
      )) as SimpleSchemaTypes.Item & { speakers?: string[] };

      // Check if the speaker is already in the story
      const existingSpeakerIds = story.speakers || [];
      if (existingSpeakerIds.includes(datoSpeakerId)) {
        return; // Speaker already in story, no need to update
      }

      this.logger.debug('Adding speaker reference to story', {
        storyId,
        datoSpeakerId,
        combined: [...existingSpeakerIds, datoSpeakerId],
      });
      // Add the speaker to the story
      await this.dClient.items.update(storyId, {
        speakers: [...existingSpeakerIds, datoSpeakerId],
      });

      // Publish the story
      await this.dClient.items.publish(storyId);
    } catch (error) {
      this.logger.error(
        'Error adding speaker reference to story',
        extractErrorInfo(error),
      );
      throw error;
    } finally {
      this.clearCache();
    }
  }

  /**
   * Remove a speaker reference from a story
   * (but keep the speaker in the album for potential reuse)
   */
  async removeSpeakerReferenceFromStoryOrAlbum(
    datoSpeakerId: string,
    storyOrAlbumId: string,
  ): Promise<void> {
    try {
      // Get the story to retrieve current speakers
      const storyOrAlbum = (await this.dClient.items.find(
        storyOrAlbumId,
      )) as SimpleSchemaTypes.Item & { speakers?: string[] };

      // Check if the speaker is in the story or album
      const existingSpeakerIds = storyOrAlbum.speakers || [];
      if (!existingSpeakerIds.includes(datoSpeakerId)) {
        return; // Speaker not in story or album, no need to update
      }

      const newSpeakers = existingSpeakerIds.filter(id => id !== datoSpeakerId);
      // Remove the speaker from the story or album
      await this.dClient.items.update(storyOrAlbumId, {
        speakers: newSpeakers.length > 0 ? newSpeakers : null,
      });

      // Publish the story or album
      await this.dClient.items.publish(storyOrAlbumId);
    } catch (error) {
      this.logger.error(
        'Error removing speaker reference from story or album',
        { error },
      );
      throw error;
    } finally {
      this.clearCache();
    }
  }

  /**
   * Update an existing speaker in DatoCMS
   *
   * @param speakerId - The ID of the speaker to update
   * @param updates - Object containing properties to update: name, title, bio, and/or voice
   * @returns The updated speaker or null if update failed
   */
  async updateSpeaker(
    speakerId: string,
    updates: {
      name?: string;
      title?: string;
      bio?: string;
      voice?: string;
      gender?: SpeakerGender | '' | null; // '' / null clears the field
      headshots?: Array<{ upload_id: string }> | null; // array of upload IDs or null to clear
    },
  ): Promise<DatoSpeaker | null> {
    try {
      // Validate that we have at least one property to update
      const hasTextUpdate =
        updates.name || updates.title || updates.bio || updates.voice;
      // Unlike the text fields, `gender` counts as an update even when blank:
      // clearing it back to unset is a meaningful edit, not a no-op.
      const hasGenderUpdate = updates.gender !== undefined;
      const hasHeadshotUpdate = updates.headshots !== undefined;
      if (!hasTextUpdate && !hasGenderUpdate && !hasHeadshotUpdate) {
        this.logger.warn('No properties provided to update speaker');
        return null;
      }

      // Create update object with only the properties that are provided
      const updateData: Record<string, unknown> = {};
      if (updates.name !== undefined) updateData.name = updates.name;
      if (updates.title !== undefined) updateData.title = updates.title;
      if (updates.bio !== undefined) updateData.bio = updates.bio;
      if (updates.voice !== undefined) updateData.voice = updates.voice;
      // DatoCMS rejects '' for an enum-validated field; send null to clear.
      if (updates.gender !== undefined) {
        updateData.gender = updates.gender || null;
      }
      if (updates.headshots !== undefined) {
        // DatoCMS gallery fields accept array of { upload_id: string } or null to clear
        updateData.headshots = updates.headshots;
      }

      // Update the speaker in DatoCMS
      const updatedSpeaker = await this.dClient.items.update(
        speakerId,
        updateData,
      );

      // Publish the speaker to make changes live
      await this.dClient.items.publish(speakerId);

      return updatedSpeaker as unknown as DatoSpeaker;
    } catch (error) {
      this.logger.error('Error updating speaker', extractErrorInfo(error));
      return null;
    } finally {
      this.clearCache();
    }
  }

  /**
   * Read a speaker's AI Voice association (PLAT-10492). Fetches the raw
   * `ai_voices` (ordered link IDs) and `ai_voice_language_prefs` (JSON pin map)
   * fields directly off the record — kept off the shared GraphQL speaker
   * queries so the common `DatoSpeaker` shape stays lean.
   */
  async getSpeakerAiVoices(speakerId: string): Promise<SpeakerAiVoiceMapping> {
    try {
      const item = (await this.dClient.items.find(
        speakerId,
      )) as SimpleSchemaTypes.Item & {
        ai_voices?: string[] | null;
        ai_voice_language_prefs?: string | Record<string, string> | null;
      };
      return {
        aiVoiceIds: item.ai_voices ?? [],
        languagePrefs: parseLanguagePrefs(item.ai_voice_language_prefs),
      };
    } catch (error) {
      this.logger.error(
        'Error fetching speaker AI voices',
        extractErrorInfo(error),
      );
      return { aiVoiceIds: [], languagePrefs: {} };
    }
  }

  /**
   * Persist a speaker's AI Voice association (PLAT-10492). Writes the ordered
   * `ai_voices` links and the `ai_voice_language_prefs` JSON pin map, then
   * publishes. The JSON field is stored as a serialized string.
   */
  async updateSpeakerAiVoices(
    speakerId: string,
    mapping: SpeakerAiVoiceMapping,
  ): Promise<boolean> {
    try {
      await this.dClient.items.update(speakerId, {
        ai_voices: mapping.aiVoiceIds,
        ai_voice_language_prefs: JSON.stringify(mapping.languagePrefs ?? {}),
      });
      await this.dClient.items.publish(speakerId);
      return true;
    } catch (error) {
      this.logger.error(
        'Error updating speaker AI voices',
        extractErrorInfo(error),
      );
      return false;
    } finally {
      this.clearCache();
    }
  }

  /**
   * Upload a headshot image to DatoCMS and return the upload object
   *
   * @param file - The image file to upload
   * @returns The DatoCMS upload object with id, or null if upload failed
   */
  async uploadHeadshot(
    file: File,
  ): Promise<{ id: string; url: string } | null> {
    try {
      const upload = await (
        this.dClient as Client
      ).uploads.createFromFileOrBlob({
        fileOrBlob: file,
        filename: file.name,
      });
      return { id: upload.id, url: upload.url };
    } catch (error) {
      this.logger.error('Error uploading headshot', extractErrorInfo(error));
      return null;
    }
  }

  clearCache(): void {
    this.cache.clear();
    this.logger.debug('Speaker cache cleared');
  }
}
